export interface OptionsType {
  domain?: string | undefined;
  encode?(value: string): string;
  expires?: Date | undefined;
  httpOnly?: boolean | undefined;
  maxAge?: number | undefined;
  path?: string | undefined;
  sameSite?: true | false | 'lax' | 'strict' | 'none' | undefined;
  secure?: boolean | undefined;
}

/**
 * Sets a client-only cookie
 *
 * @param cookieName
 * @param cookieValue
 * @param daysToExpire
 */
export function setClientCookie(
  cookieName: string,
  cookieValue: string,
  daysToExpire: number,
  domain?: string,
): void {
  const date = new Date();
  date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
  const expiresAt = `expires=${date.toUTCString()}`;
  const domainString = domain ? `;domain=${domain}` : '';
  document.cookie = `${cookieName}=${cookieValue};${expiresAt};path=/${domainString}`;
}

/**
 * Gets a client cookie value by its name
 *
 * @param cookieName
 * @returns cookie value or null if not found
 */
export function getClientCookie(cookieName: string): string | null {
  return (
    document?.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`))
      ?.split('=')[1] ?? null
  );
}

export function cookieOptions(options: OptionsType = {}, daysToExpire: number = 30): OptionsType {
  const newOptions = options;
  const date = new Date();
  date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);

  if (!options.path) {
    newOptions.path = '/';
  }

  if (!options.domain && process.env.NEXT_PUBLIC_COOKIES_DOMAIN) {
    newOptions.domain = process.env.NEXT_PUBLIC_COOKIES_DOMAIN;
  }

  newOptions.expires = date;

  return newOptions;
}

export function getParsedClientCookie<T extends Record<string, any>>(cookieName: string): T | null {
  try {
    const cookie = getClientCookie(cookieName);
    if (cookie) {
      return JSON.parse(decodeURIComponent(cookie));
    }
  } catch (err) {
    return null;
  }

  return null;
}
